import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectorsLocale } from '../../../redux/locale/localeReducer';
import { actionsOther } from '../../../redux/other/otherReducer';
import { usePaths } from '../../Routes/RouterList';
import { buildLocale } from '../../../utils/buildLocale';
import { getLocaleStorageItem, setLocaleStorageItem } from '../../../utils/localStorageHelper';
import { buildNumber, city, company, fullName, street } from '../../../constants/payData';
import AddressesDropdown from './AddressesDropdown';
import ItemInput, { MiniItemInput } from '../../Forms/Components/ItemInput';
import { IRegister } from '../../../typings/IRegister';
import { actionsDelivery, selectorsDelivery } from '../../../redux/delivery/deliveryReducer';
import { useNavigate } from 'react-router';
import validation from '../../../utils/validation';
import { selectorsUser } from '../../../redux/user/userReducer';
import { FieldValue, UseFormSetValue, UseFormTrigger, FieldErrors, FieldNamesMarkedBoolean } from 'react-hook-form';
import { formatAddress } from '../../../utils/formatAddress';

interface IProps {
  errors: FieldErrors;
  touchedFields: Partial<FieldNamesMarkedBoolean<any>>;
  register: (name: string, validation: any) => IRegister;
  debCreateDraft: () => void;
  isSelectAddress: boolean;
  setIsSelectAddress: (isSelect: boolean) => void;
  setValue: UseFormSetValue<FieldValue<any>>;
  trigger: UseFormTrigger<any>;
}

const AddressDataComponent: React.FC<IProps> = ({
  errors,
  touchedFields,
  register,
  debCreateDraft,
  setIsSelectAddress,
  isSelectAddress,
  setValue,
  trigger,
}) => {
  const paths = usePaths();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectorsUser.getDataUser);
  const currentTranslate = useSelector(selectorsLocale.getTranslate);
  const zipCode = useSelector(selectorsDelivery.getZipCode);
  const [addressData, setAddressData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const cityRegister = register('city', validation.required);
  const streetRegister = register('street', validation.required);
  const buildNumberRegister = register('buildNumber', validation.required);
  const companyRegister = register('company', {});
  const fullNameRegister = register('fullName', validation.required);

  useEffect(() => {
    setInitialAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setInitialAddress = () => {
    setAddressData((prevData: any) => ({ ...prevData, zipCode }));
    setInitialHelper(fullName);
    setInitialHelper(company);
    setInitialHelper(city);
    setInitialHelper(street);
    setInitialHelper(buildNumber);
  };

  const checkForEditModeState = (key: string, value: string | null | undefined) => {
    if (![fullName, street, buildNumber, city].includes(key)) {
      return;
    }
    if (!value?.length) {
      setIsEditMode(true);
    }
  };

  const setInitialHelper = (key: string) => {
    const keyValue = getLocaleStorageItem(key);
    let actualValue = keyValue;
    if (keyValue) {
      setAddressData((prevData: any) => ({ ...prevData, [key]: keyValue }));
      setValue(key, keyValue);
    } else if ([fullName, company].includes(key) && currentUser) {
      const currentUserValue = currentUser[key];
      actualValue = currentUserValue;
      setAddressData((prevData: any) => ({ ...prevData, [key]: currentUserValue }));
      setValue(key, currentUserValue);
      setLocaleStorageItem(key, currentUserValue || '');
    }
    checkForEditModeState(key, actualValue);
  };

  const changeData = (key: string, value: string) => {
    setLocaleStorageItem(key, value);
    setAddressData((prevData: any) => ({ ...prevData, [key]: value }));
  };

  const updateStorageAddress = (address: any) => {
    address.street = address?.street?.length ? address.street.trim() : '';
    address.city = address?.city?.length ? address.city.trim() : '';
    setLocaleStorageItem(city, address.city);
    dispatch(actionsDelivery.setZipCode(address?.zipCode || ''));
    setLocaleStorageItem(street, address.street);
    setLocaleStorageItem(buildNumber, address?.buildNumber || '');
    setInitialHelper(fullName);
    setInitialHelper(company);
    setIsSelectAddress(!isSelectAddress);
  };

  const changeZipCode = () => {
    navigate(paths.home);
    dispatch(actionsOther.setIsOpenZipCode(true));
  };

  const updateAddress = (newAddress: any) => {
    newAddress.street = newAddress.street?.length ? newAddress.street.trim() : null;
    newAddress.city = newAddress.city?.length ? newAddress.city.trim() : null;
    newAddress.buildNumber = newAddress.buildNumber?.length ? newAddress.buildNumber.trim() : null;
    setAddressData(newAddress);
    if (newAddress?.street) {
      setValue(street, newAddress.street);
      trigger(street);
    }
    if (newAddress?.city) {
      setValue(city, newAddress.city);
      trigger(city);
    }
    if (newAddress?.buildNumber) {
      setValue(buildNumber, newAddress.buildNumber);
      trigger(buildNumber);
    }
    setInitialHelper(fullName);
    setInitialHelper(company);
    [
      { key: street, value: newAddress?.street },
      { key: buildNumber, value: newAddress?.buildNumber },
      { key: city, value: newAddress?.city },
    ].forEach((item: { key: string; value: string | null | undefined }) => checkForEditModeState(item.key, item.value));
  };

  const isEditModeSwitchable = () => {
    return [addressData?.street, addressData?.buildNumber, addressData?.city, addressData?.fullName].every(
      (value: string | null | undefined) => value?.length,
    );
  };

  const onChangeEditMode = () => {
    if (isEditMode && !isEditModeSwitchable()) {
      return;
    }
    setIsEditMode(!isEditMode);
  };

  return (
    <div>
      <div className="pay-block_title">
        <div className="title">
          <span className="number">2</span> {buildLocale(currentTranslate, 'payPageAddress')}
        </div>

        <div className="checkout-edit-tools">
          <AddressesDropdown
            setAddressData={updateAddress}
            updateStorageAddress={updateStorageAddress}
            isEditMode={isEditMode}
          />
          <button
            onClick={() => onChangeEditMode()}
            className={`checkout-edit-button${isEditMode ? '_inactive' : '_active'}`}
            aria-label="Bearbeiten">
            <i className="fa fa-pencil"></i>
          </button>
        </div>
      </div>
      <div className="row">
        {isEditMode && (
          <>
            <div className="col-12 col-md-6">
              <div className="form-item">
                <label htmlFor="fullName" className="required">
                  {buildLocale(currentTranslate, 'fullName')}
                </label>
                <ItemInput
                  className="form-control"
                  errors={errors}
                  touchedFields={touchedFields}
                  defaultValue={addressData?.fullName || ''}
                  name={fullNameRegister.name}
                  ref={fullNameRegister.ref}
                  onChange={(e) => {
                    changeData(fullName, e.target.value);
                    fullNameRegister.onChange(e);
                  }}
                  onBlur={(e) => {
                    debCreateDraft();
                    fullNameRegister.onBlur(e);
                  }}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-item">
                <label htmlFor="company">{buildLocale(currentTranslate, 'tICompanyLabel')}</label>
                <ItemInput
                  errors={errors}
                  className="form-control"
                  defaultValue={addressData?.company || ''}
                  name={companyRegister.name}
                  ref={companyRegister.ref}
                  onChange={(e) => {
                    changeData(company, e.target.value);
                    companyRegister.onChange(e);
                  }}
                  onBlur={(e) => {
                    debCreateDraft();
                    companyRegister.onBlur(e);
                  }}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-item">
                <MiniItemInput
                  errors={errors}
                  label={buildLocale(currentTranslate, 'addressStreet')}
                  labelClassName="required"
                  value={addressData?.street || ''}
                  defaultValue={addressData?.street || ''}
                  name={'street'}
                  ref={streetRegister.ref}
                  onChange={(e) => {
                    changeData(street, e.target.value);
                    streetRegister.onChange(e);
                  }}
                  onBlur={(e) => {
                    debCreateDraft();
                    streetRegister.onBlur(e);
                  }}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-item">
                <MiniItemInput
                  errors={errors}
                  label={buildLocale(currentTranslate, 'addressBuild')}
                  labelClassName="required"
                  value={addressData?.buildNumber || ''}
                  defaultValue={addressData?.buildNumber || ''}
                  name={'buildNumber'}
                  ref={buildNumberRegister.ref}
                  onChange={(e) => {
                    changeData(buildNumber, e.target.value);
                    buildNumberRegister.onChange(e);
                  }}
                  onBlur={(e) => {
                    debCreateDraft();
                    buildNumberRegister.onBlur(e);
                  }}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-item">
                <MiniItemInput
                  errors={errors}
                  label={buildLocale(currentTranslate, 'addressCity')}
                  labelClassName="required"
                  value={addressData?.city || ''}
                  defaultValue={addressData?.city || ''}
                  name={'city'}
                  ref={cityRegister.ref}
                  onChange={(e) => {
                    changeData(city, e.target.value);
                    cityRegister.onChange(e);
                  }}
                  onBlur={(e) => {
                    debCreateDraft();
                    cityRegister.onBlur(e);
                  }}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-item">
                <MiniItemInput
                  errors={[]}
                  type="number"
                  label={buildLocale(currentTranslate, 'zipPlaceholder')}
                  labelClassName="required"
                  value={addressData?.zipCode || ''}
                  defaultValue={addressData?.zipCode || ''}
                  name="zipCode"
                  disabled
                />
                <span className="change-zip-link" onClick={changeZipCode}>
                  {buildLocale(currentTranslate, 'payPageChangeZipCode')}
                </span>
              </div>
            </div>
          </>
        )}
        {!isEditMode && (
          <div className="col-12 col-md-6 checkout-data-info">
            <p>
              <i className="fa fa-address-card"></i>
              {addressData?.fullName || ''}
            </p>
            {!!addressData?.company?.length && (
              <p>
                <i className="fa fa-building"></i>
                {addressData.company}
              </p>
            )}
            <p>
              <i className="fa fa-map-marker"></i>
              {addressData ? formatAddress(addressData) : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressDataComponent;
